import { z } from 'zod';
import { SELECTOR_PACK_LIMITS } from './constants';
import { locatorCandidateSchema, type LocatorCandidate } from './locator';

/**
 * Detector conditions — how the agent decides "is this a login page?", "did we
 * hit a checkpoint?", "is the session active?".
 *
 * Two deliberate omissions:
 *
 *  - **No regular expressions.** A pack is published by an admin and evaluated on
 *    every client machine; a catastrophically backtracking pattern would be a
 *    remote denial-of-service against users. Substring and prefix tests cover the
 *    real cases.
 *  - **No script conditions.** Same reason as everywhere else in this package:
 *    the config channel must never become a code channel.
 *
 * Detection here is strictly *observational*. The system reports a checkpoint or
 * 2FA prompt and hands control back to the human — it never attempts to solve,
 * bypass, or evade one.
 */

const textValue = z.string().min(1).max(SELECTOR_PACK_LIMITS.maxTextLength);

export type Condition =
  | { type: 'urlContains'; value: string }
  | { type: 'urlPathStartsWith'; value: string }
  | { type: 'titleContains'; value: string }
  | { type: 'textVisible'; value: string }
  | { type: 'selectorVisible'; locator: LocatorCandidate }
  | { type: 'selectorAbsent'; locator: LocatorCandidate }
  | { type: 'anyOf'; conditions: Condition[] }
  | { type: 'allOf'; conditions: Condition[] }
  | { type: 'not'; condition: Condition };

/**
 * Recursive schema. `z.lazy` handles the self-reference; the depth cap is applied
 * separately by {@link assertConditionDepth} because Zod cannot express it.
 */
export const conditionSchema: z.ZodType<Condition> = z.lazy(() =>
  z.discriminatedUnion('type', [
    z.object({ type: z.literal('urlContains'), value: textValue }).strict(),
    z.object({ type: z.literal('urlPathStartsWith'), value: textValue }).strict(),
    z.object({ type: z.literal('titleContains'), value: textValue }).strict(),
    z.object({ type: z.literal('textVisible'), value: textValue }).strict(),
    z.object({ type: z.literal('selectorVisible'), locator: locatorCandidateSchema }).strict(),
    z.object({ type: z.literal('selectorAbsent'), locator: locatorCandidateSchema }).strict(),
    z
      .object({
        type: z.literal('anyOf'),
        conditions: z.array(conditionSchema).min(1).max(SELECTOR_PACK_LIMITS.maxConditionChildren),
      })
      .strict(),
    z
      .object({
        type: z.literal('allOf'),
        conditions: z.array(conditionSchema).min(1).max(SELECTOR_PACK_LIMITS.maxConditionChildren),
      })
      .strict(),
    z.object({ type: z.literal('not'), condition: conditionSchema }).strict(),
  ]),
) as z.ZodType<Condition>;

/** Depth of a condition tree, counting the root as 1. */
export function conditionDepth(condition: Condition): number {
  switch (condition.type) {
    case 'anyOf':
    case 'allOf':
      return 1 + Math.max(...condition.conditions.map(conditionDepth));
    case 'not':
      return 1 + conditionDepth(condition.condition);
    default:
      return 1;
  }
}

/**
 * Reject condition trees deeper than the limit. Guards against a pack that would
 * cost an unbounded number of DOM queries to evaluate.
 */
export function assertConditionDepth(condition: Condition, key: string): void {
  const depth = conditionDepth(condition);
  if (depth > SELECTOR_PACK_LIMITS.maxConditionDepth) {
    throw new Error(
      `Detector "${key}" nests ${depth} levels deep; the limit is ${SELECTOR_PACK_LIMITS.maxConditionDepth}.`,
    );
  }
}

export const detectorRuleSchema = z
  .object({
    condition: conditionSchema,
    description: z.string().max(300).optional(),
  })
  .strict();

export type DetectorRule = z.infer<typeof detectorRuleSchema>;
